/**
 * Metro Coffee — main.js
 * GSAP + Lenis premium editorial interactions
 */

import Lenis from 'lenis';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

/* ═══════════════════════════════════════════════════
   1. LENIS SMOOTH SCROLL
══════════════════════════════════════════════════ */
function initLenis() {
  const lenis = new Lenis({
    duration: 1.45,
    easing: t => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
    smoothWheel: true,
    smoothTouch: false,
    wheelMultiplier: 1,
  });

  lenis.on('scroll', ScrollTrigger.update);

  gsap.ticker.add(time => lenis.raf(time * 1000));
  gsap.ticker.lagSmoothing(0);

  // Smooth anchor-link scrolling
  document.querySelectorAll('a[href^="#"]').forEach(anchor => {
    anchor.addEventListener('click', e => {
      const id = anchor.getAttribute('href');
      const target = id === '#' ? document.body : document.querySelector(id);
      if (!target) return;
      e.preventDefault();
      lenis.scrollTo(target, { offset: -72, duration: 1.6 });
    });
  });

  return lenis;
}

/* ═══════════════════════════════════════════════════
   2. INTRO ANIMATION — SVG cup draws then reveals hero
══════════════════════════════════════════════════ */
function initIntro() {
  const intro = document.getElementById('intro');
  if (!intro) return;

  const svg   = document.getElementById('intro-svg');
  const paths = svg.querySelectorAll('.svg-draw');

  // ── Pre-set initial states ─────────────────────
  // Hide hero elements before intro ends
  gsap.set([
    '.hero-eyebrow',
    '.hero-line',
    '.hero-cta-row',
  ], { opacity: 0, y: 20 });

  // Set up stroke drawing for each path
  paths.forEach(path => {
    try {
      const len = path.getTotalLength();
      gsap.set(path, {
        strokeDasharray: len,
        strokeDashoffset: len,
      });
    } catch (_) {
      /* SVG might not have a measurable length (rare) */
    }
  });

  // ── Master timeline ────────────────────────────
  const tl = gsap.timeline({
    defaults: { ease: 'power2.out' },
    onComplete: () => {
      // Remove intro from DOM after animation
      intro.remove();
    },
  });

  // Draw each path in sequence
  tl.to(paths, {
    strokeDashoffset: 0,
    duration: 1.3,
    stagger: 0.14,
    ease: 'power2.inOut',
  })

  // Small pause — appreciate the complete cup
  .to({}, { duration: 0.35 })

  // Fade out + tiny scale up of SVG as it exits
  .to(svg, {
    opacity: 0,
    scale: 1.06,
    duration: 0.45,
    ease: 'power2.in',
  })

  // Fade out intro overlay
  .to(intro, {
    opacity: 0,
    duration: 0.4,
    ease: 'power1.in',
    pointerEvents: 'none',
  }, '-=0.3')

  // ── Reveal hero text ────────────────────────────
  .to('.hero-eyebrow', {
    opacity: 1, y: 0,
    duration: 0.7,
    ease: 'power3.out',
  }, '-=0.05')

  .to('.hero-line', {
    opacity: 1, y: 0,
    duration: 0.95,
    stagger: 0.14,
    ease: 'power3.out',
  }, '-=0.5')

  .to('.hero-cta-row', {
    opacity: 1, y: 0,
    duration: 0.7,
    ease: 'power2.out',
  }, '-=0.55');
}

/* ═══════════════════════════════════════════════════
   3. NAVIGATION
══════════════════════════════════════════════════ */
function initNav() {
  const nav     = document.getElementById('nav');
  const ham     = document.querySelector('.nav-hamburger');
  const canvas  = document.getElementById('offcanvas');
  const overlay = document.getElementById('offcanvas-overlay');
  const closeBtn= document.querySelector('.offcanvas-close');

  if (!nav || !ham || !canvas) return;

  // ── Scroll-based nav state ─────────────────────
  ScrollTrigger.create({
    start: 'top -80',
    onUpdate: self => {
      nav.classList.toggle('nav--scrolled', self.progress > 0);
    },
  });

  // ── Off-canvas open / close ────────────────────
  function openMenu() {
    gsap.set(canvas, { x: '-100%' });
    canvas.setAttribute('aria-hidden', 'false');
    overlay.classList.add('is-visible');
    ham.setAttribute('aria-expanded', 'true');
    document.body.style.overflow = 'hidden';

    gsap.to(canvas, {
      x: 0,
      duration: 0.55,
      ease: 'power4.out',
    });
  }

  function closeMenu() {
    overlay.classList.remove('is-visible');
    ham.setAttribute('aria-expanded', 'false');

    gsap.to(canvas, {
      x: '-100%',
      duration: 0.4,
      ease: 'power3.in',
      onComplete: () => {
        canvas.setAttribute('aria-hidden', 'true');
        document.body.style.overflow = '';
      },
    });
  }

  ham.addEventListener('click', openMenu);
  overlay.addEventListener('click', closeMenu);
  if (closeBtn) closeBtn.addEventListener('click', closeMenu);

  canvas.querySelectorAll('a').forEach(a => a.addEventListener('click', closeMenu));
}

/* ═══════════════════════════════════════════════════
   4. GALLERY — subtle parallax as images scroll into view
══════════════════════════════════════════════════ */
function initGallery() {
  const isDesktop = window.matchMedia('(min-width: 641px)').matches;

  // Fade gallery in as the images start to peek into view
  gsap.from('#gallery', {
    opacity: 0,
    duration: 0.9,
    ease: 'power2.out',
    scrollTrigger: {
      trigger: '#gallery',
      start: 'top 100%',
    },
  });

  if (!isDesktop) return;

  /*
    Each image already has a CSS translateY offset creating staggered peek heights.
    GSAP adds a *small additional* Y drift per image on scroll,
    so each image moves at a slightly different speed — reinforcing depth.
  */
  const parallaxMap = [
    { selector: '.gi-1', additionalY: -28 },
    { selector: '.gi-2', additionalY:  18 },
    { selector: '.gi-3', additionalY: -18 },
    { selector: '.gi-4', additionalY:  32 },
  ];

  parallaxMap.forEach(({ selector, additionalY }) => {
    const el = document.querySelector(selector);
    if (!el) return;
    gsap.to(el, {
      y: additionalY,
      ease: 'none',
      scrollTrigger: {
        trigger: '#gallery',
        start: 'top bottom',
        end: 'bottom top',
        scrub: 2.5,
      },
    });
  });
}


/* ═══════════════════════════════════════════════════
   5. STORY SECTION REVEALS
══════════════════════════════════════════════════ */
function initStory() {
  // Primary photo reveal
  gsap.from('.story-photo--primary', {
    scale: 0.95,
    opacity: 0,
    duration: 1.2,
    ease: 'power2.out',
    scrollTrigger: {
      trigger: '.story-statement',
      start: 'top 82%',
    },
  });

  // Headline reveal
  gsap.from('.story-headline', {
    y: 48,
    opacity: 0,
    duration: 1.0,
    ease: 'power3.out',
    scrollTrigger: {
      trigger: '.story-statement',
      start: 'top 76%',
    },
  });

  gsap.from('.story-italic', {
    y: 32,
    opacity: 0,
    duration: 0.85,
    ease: 'power3.out',
    delay: 0.18,
    scrollTrigger: {
      trigger: '.story-statement',
      start: 'top 76%',
    },
  });

  // Illustration parallax (subtle upward drift)
  gsap.to('.story-illustration', {
    y: -48,
    ease: 'none',
    scrollTrigger: {
      trigger: '#story',
      start: 'top bottom',
      end: 'bottom top',
      scrub: 2.5,
    },
  });

  // Body copy reveal
  const copyParagraphs = document.querySelectorAll('.story-copy p, .story-copy .text-link');
  gsap.from(copyParagraphs, {
    y: 28,
    opacity: 0,
    duration: 0.8,
    stagger: 0.14,
    ease: 'power2.out',
    scrollTrigger: {
      trigger: '.story-body',
      start: 'top 82%',
    },
  });

  // Secondary photo
  gsap.from('.story-photo--secondary', {
    scale: 0.95,
    opacity: 0,
    duration: 1.1,
    ease: 'power2.out',
    scrollTrigger: {
      trigger: '.story-body',
      start: 'top 78%',
    },
  });
}

/* ═══════════════════════════════════════════════════
   6. MENU — HOVER IMAGE PANEL
══════════════════════════════════════════════════ */
function initMenu() {
  // Menu title + tag reveal
  gsap.from(['.menu-tag', '.menu-title'], {
    y: 36,
    opacity: 0,
    duration: 0.9,
    stagger: 0.12,
    ease: 'power3.out',
    scrollTrigger: {
      trigger: '.menu-masthead',
      start: 'top 82%',
    },
  });

  // Category reveals
  gsap.from('.menu-cat', {
    y: 28,
    opacity: 0,
    duration: 0.75,
    stagger: 0.1,
    ease: 'power2.out',
    scrollTrigger: {
      trigger: '#menu',
      start: 'top 70%',
    },
  });

  // ── Menu Pagination (Pages 1, 2, 3) ────────────
  const tabBtns = document.querySelectorAll('.menu-tab-btn');
  const pages = document.querySelectorAll('.menu-page');
  const prevBtn = document.getElementById('menu-prev-btn');
  const nextBtn = document.getElementById('menu-next-btn');
  const indicator = document.getElementById('menu-page-indicator');
  const totalPages = 3;
  let currentPage = 1;

  function updatePagination(newPage) {
    if (newPage < 1 || newPage > totalPages) return;
    currentPage = newPage;

    // Update active tab button
    tabBtns.forEach(btn => {
      const page = parseInt(btn.getAttribute('data-page'), 10);
      const isActive = page === currentPage;
      btn.classList.toggle('is-active', isActive);
      btn.setAttribute('aria-selected', isActive ? 'true' : 'false');
    });

    // Update active page container
    pages.forEach(pg => {
      const isTarget = pg.id === `menu-page-${currentPage}`;
      pg.classList.toggle('is-active', isTarget);
    });

    // Update indicator and buttons
    if (indicator) {
      indicator.textContent = `Page ${currentPage} of ${totalPages}`;
    }
    if (prevBtn) {
      prevBtn.disabled = currentPage === 1;
    }
    if (nextBtn) {
      nextBtn.disabled = currentPage === totalPages;
    }

    // Refresh ScrollTrigger since DOM flow changes
    ScrollTrigger.refresh();

    // Update featured image based on current page
    if (typeof updateFeaturedForPage === 'function') {
      updateFeaturedForPage(currentPage);
    }
  }

  tabBtns.forEach(btn => {
    btn.addEventListener('click', () => {
      const page = parseInt(btn.getAttribute('data-page'), 10);
      if (!isNaN(page)) {
        updatePagination(page);
      }
    });
  });

  if (prevBtn) {
    prevBtn.addEventListener('click', () => {
      if (currentPage > 1) {
        updatePagination(currentPage - 1);
        const menuElem = document.getElementById('menu');
        if (menuElem && window.scrollY > menuElem.offsetTop + 100) {
          menuElem.scrollIntoView({ behavior: 'smooth' });
        }
      }
    });
  }

  if (nextBtn) {
    nextBtn.addEventListener('click', () => {
      if (currentPage < totalPages) {
        updatePagination(currentPage + 1);
        const menuElem = document.getElementById('menu');
        if (menuElem && window.scrollY > menuElem.offsetTop + 100) {
          menuElem.scrollIntoView({ behavior: 'smooth' });
        }
      }
    });
  }

  // ── Hover panel interaction (desktop only) ─────
  const isDesktop = window.matchMedia('(min-width: 961px)').matches;
  if (!isDesktop) return;

  const items      = document.querySelectorAll('.menu-item');
  const panel      = document.getElementById('menu-panel');
  const panelPhoto = document.getElementById('menu-panel-photo');
  const panelCat   = document.getElementById('menu-panel-cat');
  const panelLbl   = document.getElementById('menu-panel-lbl');

  if (!panel) return;

  const pageDefaults = {
    1: { label: 'CAPPUCCINO', img: '/menu/cappuccino.jpg', cat: 'Hot Drinks — Coffee' },
    2: { label: 'ICE LATTE', img: '/menu/ice-latte.jpg', cat: 'Cold Drinks — Coffee' },
    3: { label: 'FRUIT WAFFLE', img: '/menu/waffle.jpg', cat: 'Desserts & Waffles' }
  };

  function setFeaturedItem(label, img, cat) {
    if (panelLbl && label) panelLbl.textContent = label;
    if (panelCat && cat) panelCat.textContent = cat;
    if (panelPhoto && img) {
      if (!panelPhoto.src.endsWith(img)) {
        gsap.to(panelPhoto, {
          opacity: 0.35,
          duration: 0.15,
          ease: 'power1.out',
          onComplete: () => {
            panelPhoto.src = img;
            gsap.to(panelPhoto, { opacity: 1, duration: 0.28, ease: 'power2.out' });
          }
        });
      }
    }
  }

  function updateFeaturedForPage(pageNum) {
    const def = pageDefaults[pageNum];
    if (def) {
      setFeaturedItem(def.label, def.img, def.cat);
    }
  }

  items.forEach(item => {
    item.addEventListener('mouseenter', () => {
      const label = item.getAttribute('data-panel-label') || '';
      const img   = item.getAttribute('data-panel-img') || '/menu/cappuccino.jpg';
      const cat   = item.getAttribute('data-panel-cat') || 'Crafted in Mombasa';
      setFeaturedItem(label, img, cat);
    });
  });
}

/* ═══════════════════════════════════════════════════
   7. METRO MOMENT SECTION
══════════════════════════════════════════════════ */
function initMoment() {
  gsap.from('.moment-tag', {
    y: 20,
    opacity: 0,
    duration: 0.7,
    ease: 'power2.out',
    scrollTrigger: { trigger: '#moment', start: 'top 80%' },
  });

  gsap.from('.moment-media', {
    scale: 0.96,
    opacity: 0,
    duration: 1.1,
    ease: 'power2.out',
    scrollTrigger: { trigger: '#moment', start: 'top 75%' },
  });

  gsap.from('.moment-caption', {
    y: 18,
    opacity: 0,
    duration: 0.7,
    ease: 'power2.out',
    scrollTrigger: { trigger: '.moment-media', start: 'center 75%' },
  });
}

/* ═══════════════════════════════════════════════════
   8. SOCIAL SECTION
══════════════════════════════════════════════════ */
function initSocial() {
  // Entrance animations
  gsap.from('.social-title', {
    y: 36,
    opacity: 0,
    duration: 0.9,
    ease: 'power3.out',
    scrollTrigger: { trigger: '#social', start: 'top 80%' },
  });

  gsap.from('.social-nav', {
    y: 16,
    opacity: 0,
    duration: 0.7,
    ease: 'power2.out',
    scrollTrigger: { trigger: '#social', start: 'top 80%' },
  });

  gsap.from('.ig-card', {
    y: 28,
    opacity: 0,
    duration: 0.75,
    stagger: 0.08,
    ease: 'power2.out',
    scrollTrigger: { trigger: '.ig-grid', start: 'top 85%' },
  });

  gsap.from('.social-platforms', {
    opacity: 0,
    duration: 0.6,
    ease: 'power2.out',
    scrollTrigger: { trigger: '.social-platforms', start: 'top 88%' },
  });

  // Tab switching between Instagram & TikTok
  const tabBtns = document.querySelectorAll('.social-tab-btn');
  const panels = {
    instagram: document.getElementById('panel-instagram'),
    tiktok: document.getElementById('panel-tiktok'),
  };

  tabBtns.forEach(btn => {
    btn.addEventListener('click', () => {
      const tabName = btn.getAttribute('data-tab');
      if (!tabName || !panels[tabName]) return;

      tabBtns.forEach(b => {
        b.classList.remove('is-active');
        b.setAttribute('aria-selected', 'false');
      });
      btn.classList.add('is-active');
      btn.setAttribute('aria-selected', 'true');

      Object.values(panels).forEach(p => {
        if (p) p.classList.remove('is-active');
      });

      panels[tabName].classList.add('is-active');

      // Refresh ScrollTrigger calculations after tab switch
      ScrollTrigger.refresh();
    });
  });
}

/* ═══════════════════════════════════════════════════
   9. LOCATIONS — TAB SWITCHER + MAP
══════════════════════════════════════════════════ */
function initLocations() {
  const tabs     = document.querySelectorAll('.loc-tab');
  const map      = document.getElementById('loc-map');
  const panelIds = { digo: 'loc-panel-digo', moi: 'loc-panel-moi' };
  const mapUrls  = {
    digo: 'https://maps.google.com/maps?q=Digo+Road,+Mombasa,+Kenya&t=&z=15&ie=UTF8&iwloc=&output=embed',
    moi:  'https://maps.google.com/maps?q=Moi+Avenue,+Mombasa,+Kenya&t=&z=15&ie=UTF8&iwloc=&output=embed',
  };

  tabs.forEach(tab => {
    tab.addEventListener('click', () => {
      const loc = tab.getAttribute('data-loc');

      // Update tab active state + ARIA
      tabs.forEach(t => {
        t.classList.remove('loc-tab--active');
        t.setAttribute('aria-selected', 'false');
      });
      tab.classList.add('loc-tab--active');
      tab.setAttribute('aria-selected', 'true');

      // Switch detail panels
      Object.values(panelIds).forEach(id => {
        document.getElementById(id)?.classList.add('loc-detail--hidden');
      });
      const activePanel = document.getElementById(panelIds[loc]);
      if (activePanel) {
        activePanel.classList.remove('loc-detail--hidden');
        gsap.fromTo(activePanel,
          { opacity: 0, y: 12 },
          { opacity: 1, y: 0, duration: 0.45, ease: 'power2.out' }
        );
      }

      // Update map src
      if (map && mapUrls[loc]) {
        map.src = mapUrls[loc];
      }
    });
  });

  // Section reveal animations
  gsap.from('.locations-title', {
    y: 40,
    opacity: 0,
    duration: 0.9,
    ease: 'power3.out',
    scrollTrigger: { trigger: '#locations', start: 'top 80%' },
  });

  gsap.from('.loc-sidebar', {
    y: 28,
    opacity: 0,
    duration: 0.85,
    ease: 'power2.out',
    scrollTrigger: { trigger: '.locations-layout', start: 'top 82%' },
  });

  gsap.from('.loc-map-wrap', {
    scale: 0.97,
    opacity: 0,
    duration: 1.0,
    ease: 'power2.out',
    scrollTrigger: { trigger: '.locations-layout', start: 'top 78%' },
  });
}

/* ═══════════════════════════════════════════════════
   10. FOOTER
══════════════════════════════════════════════════ */
function initFooter() {
  // "SEE YOU SOON." — large statement reveal
  gsap.from('.footer-statement', {
    y: 60,
    opacity: 0,
    duration: 1.2,
    ease: 'power3.out',
    scrollTrigger: {
      trigger: '#footer',
      start: 'top 85%',
    },
  });

  gsap.from('.footer-body', {
    y: 24,
    opacity: 0,
    duration: 0.8,
    ease: 'power2.out',
    scrollTrigger: {
      trigger: '.footer-body',
      start: 'top 90%',
    },
  });
}

/* ═══════════════════════════════════════════════════
   INIT ALL
══════════════════════════════════════════════════ */
document.addEventListener('DOMContentLoaded', () => {
  initLenis();
  initIntro();
  initNav();
  initGallery();
  initStory();
  initMenu();
  initMoment();
  initSocial();
  initLocations();
  initFooter();
});
